import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, getErrorMessage } from '@/api/api';
import { useToast } from '@/context/ToastContext';
import type { CreateEchoDraftRequest, EchoDraftResponse } from '@/types/echo_creation';

interface UseCreateEchoDraftReturn {
  draftId: string | null;
  draftStatus: EchoDraftResponse['status'] | null;
  draft: EchoDraftResponse | null;
  isCreating: boolean;
  isLoadingDraft: boolean;
  createDraft: (data: CreateEchoDraftRequest) => Promise<void>;
  refreshDraft: () => Promise<void>;
  searchParams: URLSearchParams;
  setSearchParams: ReturnType<typeof useSearchParams>[1];
}

interface ApiErrorWithStatus {
  status?: number;
}

/** Manages draft lifecycle: resume from URL, create/update via API. */
export const useCreateEchoDraft = (): UseCreateEchoDraftReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const draftIdFromUrl = searchParams.get('draftId');

  const [draftId, setDraftId] = React.useState<string | null>(draftIdFromUrl);
  const [draftStatus, setDraftStatus] = React.useState<EchoDraftResponse['status'] | null>(null);
  const [draft, setDraft] = React.useState<EchoDraftResponse | null>(null);
  const [isCreating, setIsCreating] = React.useState<boolean>(false);

  const [isLoadingDraft, setIsLoadingDraft] = React.useState<boolean>(!!draftIdFromUrl);
  const { addToast } = useToast();
  const loadedIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!draftIdFromUrl) {
      setIsLoadingDraft(false);
      return;
    }

    if (loadedIdRef.current === draftIdFromUrl) {
      setIsLoadingDraft(false);
      return;
    }

    let isMounted = true;

    const resumeDraft = async () => {
      try {
        if (typeof api.echoes.getDraftProfile !== 'function') {
          throw new Error('API method "getDraftProfile" is missing in api.ts');
        }

        const profile = await api.echoes.getDraftProfile(draftIdFromUrl);

        if (isMounted) {
          setDraftId(profile.id);
          setDraftStatus(profile.status);
          setDraft(profile);
          loadedIdRef.current = draftIdFromUrl;
        }
      } catch {
        if (isMounted) {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('draftId');
            return next;
          });
          setDraftId(null);

          addToast({
            title: 'Could not resume draft',
            subtitle: 'Starting fresh session.',
            type: 'info',
          });
        }
      } finally {
        if (isMounted) {
          setIsLoadingDraft(false);
        }
      }
    };

    setIsLoadingDraft(true);
    resumeDraft();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftIdFromUrl, addToast]);

  const createDraft = async (data: CreateEchoDraftRequest) => {
    setIsCreating(true);
    try {
      let activeDraftId = draftId;

      if (activeDraftId) {
        try {
          const response = await api.echoes.updateBasicInfo(activeDraftId, data);
          setDraft(response);
          setDraftStatus(response.status);

          addToast({
            title: 'Draft Updated',
            subtitle: 'Changes saved successfully.',
            type: 'success',
          });
          return;
        } catch (err: unknown) {
          const apiError = err as ApiErrorWithStatus;
          if (apiError?.status === 404) {
            activeDraftId = null;
          } else {
            const msg = getErrorMessage(err);
            addToast({
              title: 'Update Failed',
              subtitle: msg,
              type: 'error',
            });
            throw err;
          }
        }
      }

      const response = await api.echoes.createDraft(data);

      setDraftId(response.id);
      setDraftStatus(response.status);
      setDraft(response);
      loadedIdRef.current = response.id;

      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('draftId', response.id);
        return next;
      });

      if (data.name) {
        addToast({
          title: 'Draft Created',
          subtitle: `Started creating ${data.name}`,
          type: 'success',
        });
      }
    } catch (error) {
      const msg = getErrorMessage(error);
      addToast({
        title: 'Operation Failed',
        subtitle: msg,
        type: 'error',
      });
      throw error;
    } finally {
      setIsCreating(false);
    }
  };

  const refreshDraft = React.useCallback(async () => {
    const id = draftId;
    if (!id) return;
    try {
      const profile = await api.echoes.getDraftProfile(id);
      setDraft(profile);
      setDraftStatus(profile.status);
    } catch (err) {
      const msg = getErrorMessage(err);
      addToast({
        title: 'Sync Failed',
        subtitle: msg || 'Could not refresh draft.',
        type: 'error',
      });
    }
  }, [draftId, addToast]);

  return {
    draftId,
    draftStatus,
    draft,
    isCreating,
    createDraft,
    isLoadingDraft,
    refreshDraft,
    searchParams,
    setSearchParams,
  };
};

export default useCreateEchoDraft;
